import dayjs from 'dayjs'
import type { BetSource } from '@mobi/betslip/types'
import type { MysteryBetTypes } from '@classic/Betting-v2/DataTransferObjects/MysteryDataTransferObject'
import { BetType } from '@classic/Betting-v2/Model/Betting/BetType'
import { publish } from '@classic/AppUtils/Framework/Messaging/EventAggregator'
import { appHistory } from '@core/Utils/routing/history'
import { ReplaceCurrentUrl } from '@core/State/Navigation/signals'

const enum LocalConstants {
  BetTypeParam = 'betType',
  StartersParam = 'starters',
  StarterParam = 'starter',
  SourceParam = 'source',
}

export function getTypeAndStartersFromRaceUri(): {
  betType: BetType
  starters: RaceStarter[]
  isValidUri: boolean
  source?: BetSource
} {
  const currentSearchParams = new URLSearchParams(window.location.search)

  // Capture incoming search params
  const betTypeParam = currentSearchParams.get(LocalConstants.BetTypeParam)
  const startersParam = currentSearchParams.get(LocalConstants.StartersParam)
  const starterParam = currentSearchParams.get(LocalConstants.StarterParam)
  const sourceParam = currentSearchParams.get(LocalConstants.SourceParam)

  // Clean searched params to prevent re-opening Quickbet
  currentSearchParams.delete(LocalConstants.BetTypeParam)
  currentSearchParams.delete(LocalConstants.StartersParam)
  currentSearchParams.delete(LocalConstants.StarterParam)
  currentSearchParams.delete(LocalConstants.SourceParam)

  const newQueryString = currentSearchParams.toString()
  const newUrl = window.location.pathname + '?' + newQueryString
  appHistory.replace(newUrl)
  ReplaceCurrentUrl({ url: newUrl, isRacePath: true })

  const hasValidBetType = !!betTypeParam && typeof betTypeParam === 'string'

  const betType: BetType = hasValidBetType
    ? BetType[betTypeParam as MysteryBetTypes] || BetType.WinPlace
    : BetType.WinPlace

  const starters =
    betType === BetType.SameRaceMulti
      ? parseSrmStartersFromToteRaceUri(startersParam)
      : parseWinAndPlaceStarterFromToteRaceUri(starterParam)

  const isValidUri = hasValidBetType || (!!starters && starters.length > 0)

  return {
    source: (sourceParam as BetSource) || undefined,
    betType,
    isValidUri,
    starters,
  }
}

export function buildToteRaceUri({
  meetingId,
  raceNumber,
  meetingDate,
  starter,
  source,
}: {
  meetingId: string
  raceNumber: number
  meetingDate: string
  starter?: number
  source?: string
}) {
  const formattedMeetingDate = dayjs(meetingDate).format('YYYY-MM-DD')
  let url = `/tote/meetings/${meetingId.toLowerCase()}/${raceNumber}?date=${formattedMeetingDate}`
  if (starter) url += `&betType=WinPlace&starter=${starter}`
  if (source) url += `&source=${source}`
  return url
}

export function buildSameRaceMultiRaceUri({
  meetingId,
  raceNumber,
  meetingDate,
  starters,
  source,
}: {
  meetingId: string
  raceNumber: number
  meetingDate: string
  starters?: string
  source?: string
}): string {
  const formattedMeetingDate = dayjs(meetingDate).format('YYYY-MM-DD')
  let url = `/tote/meetings/${meetingId.toLowerCase()}/${raceNumber}?date=${formattedMeetingDate}&betType=SameRaceMulti`
  if (starters) url += `&starters=${starters}`
  if (source) url += `&source=${source}`
  return url
}

export function setSelectedRace(raceNumber: number): void {
  publish('race-navigate-command', {
    raceNumber,
  })
}

export const getPayIndicatorText = (payIndicator?: number): string | null => {
  if (!payIndicator) return null

  if (payIndicator === 1) return 'Pay Win Only'

  let text = 'Pay '

  for (let i = 1; i <= payIndicator; i++) {
    text += i + ','
  }

  return text.slice(0, -1)
}

// =============
// Local Helpers
// =============

function parseWinAndPlaceStarterFromToteRaceUri(starter: string | null): RaceStarter[] {
  if (!starter || isNaN(Number(starter))) return []
  return [{ starterNumber: Number(starter), place: 1 }]
}

function parseSrmStartersFromToteRaceUri(starters: string | null): RaceStarter[] {
  if (!starters) return []

  const pairs = starters.split(',')
  const pairRegex = /^(?<starter>\d+)-(?<leg>\d+)$/

  return pairs.reduce((existing, newPair) => {
    const splits = newPair.match(pairRegex)
    if (splits && splits.groups) {
      existing.push({
        starterNumber: parseInt(splits.groups['starter']),
        legNumber: parseInt(splits.groups['leg']),
      } as RaceStarter)
    }
    return existing
  }, [] as RaceStarter[])
}

// =====
// Types
// =====

interface RaceStarter {
  starterNumber: number
  place?: number
  legNumber?: number
}
